import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Icon } from '@carrent/ui'
import { pathTo, useKbPage, useKbSearch, useKbSpaces, useKbTree } from '@carrent/domain'
import { kbApi } from '@carrent/api'
import { PageTree } from './PageTree'
import { ArticleView } from './ArticleView'
import { ArticleEditor } from './ArticleEditor'
import { HistoryPanel } from './HistoryPanel'
import { ReviewQueue } from './ReviewQueue'
import { SpaceSettings } from './SpaceSettings'

/**
 * База знаний в консоли.
 *
 * Три колонки: пространства — дерево страниц — статья. Это рабочий
 * экран сотрудника: он держит в поле зрения и структуру, и текст,
 * и переключается между разделами без возвратов назад. Список карточек
 * такой работы не выдерживает — до нужного пункта регламента пришлось бы
 * идти через три экрана.
 *
 * Адрес хранит и пространство, и статью (/admin/kb/REGL/12), поэтому
 * ссылку на статью можно отправить коллеге.
 */
export default function KnowledgePage() {
  const { spaceKey, pageId } = useParams()
  const navigate = useNavigate()

  const { spaces, loading: spacesLoading, error: spacesError } = useKbSpaces()
  const [query, setQuery] = useState('')
  // Режим правой колонки: чтение, правка или очередь проверки.
  const [mode, setMode] = useState('read')
  const [showHistory, setShowHistory] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [creating, setCreating] = useState(false)
  const [pendingCount, setPendingCount] = useState(0)

  // Пространство не выбрано — открываем первое доступное.
  useEffect(() => {
    if (!spaceKey && spaces.length) {
      navigate(`/admin/kb/${spaces[0].key}`, { replace: true })
    }
  }, [spaceKey, spaces, navigate])

  const {
    space, pages, tree, loading: treeLoading, error: treeError, reload: reloadTree,
  } = useKbTree(spaceKey)
  const activeId = pageId ? Number(pageId) : null
  const { page, loading: pageLoading, error: pageError, reload: reloadPage } = useKbPage(activeId)

  const level = space?.my_level || 'read'
  const canWrite = level === 'write' || level === 'admin'
  const canManage = level === 'admin'

  // Счётчик правок на проверке — только для тех, кто их проверяет.
  useEffect(() => {
    if (!canManage) {
      setPendingCount(0)
      return
    }
    kbApi.reviewQueue()
      .then((r) => setPendingCount((r.data?.data || []).length))
      .catch(() => setPendingCount(0))
  }, [canManage, mode])

  // Смена статьи возвращает в режим чтения: остаться в редакторе
  // чужой страницы — верный способ потерять правку.
  useEffect(() => { setMode('read') }, [activeId, spaceKey])

  const { results, loading: searching } = useKbSearch(query)
  const breadcrumbs = useMemo(
    () => (activeId ? pathTo(pages, activeId) : []),
    [pages, activeId],
  )

  const openPage = (p) => navigate(`/admin/kb/${spaceKey}/${p.id}`)

  const createPage = async (parentId = null) => {
    const title = prompt('Название новой статьи')
    if (!title || !title.trim()) return
    setCreating(true)
    try {
      const r = await kbApi.createPage({
        space_key: spaceKey,
        title: title.trim(),
        parent_id: parentId,
        body_md: '',
      })
      reloadTree()
      navigate(`/admin/kb/${spaceKey}/${r.data.data.page.id}`)
      setMode('edit')
    } catch (e) {
      alert(e.response?.data?.message || 'Не удалось создать статью')
    } finally {
      setCreating(false)
    }
  }

  const archivePage = async () => {
    if (!confirm(`Убрать «${page.title}» в архив? Статья исчезнет из дерева и поиска.`)) return
    try {
      await kbApi.archive(page.id)
      reloadTree()
      navigate(`/admin/kb/${spaceKey}`)
    } catch (e) {
      alert(e.response?.data?.message || 'Не удалось архивировать')
    }
  }

  const togglePinned = async () => {
    try {
      await kbApi.updatePage(page.id, { pinned: !page.pinned })
      reloadPage()
      reloadTree()
    } catch (e) {
      alert(e.response?.data?.message || 'Не удалось изменить закрепление')
    }
  }
  const openHit = (hit) => {
    const target = spaces.find((s) => s.id === hit.space_id)
    if (target) navigate(`/admin/kb/${target.key}/${hit.page_id}`)
    setQuery('')
  }

  if (spacesLoading) {
    return (
      <div className="kb-boot">
        <div className="spinner" />
      </div>
    )
  }

  if (spacesError) {
    return (
      <div className="kb-state">
        <Icon name="warning" size={28} aria-hidden="true" />
        <p>{spacesError}</p>
      </div>
    )
  }

  if (!spaces.length) {
    return (
      <div className="kb-state">
        <Icon name="file" size={28} aria-hidden="true" />
        <p className="kb-state__title">Доступных пространств нет</p>
        <p className="kb-state__hint">
          Доступ к базе знаний выдаёт администратор — по группе или лично.
        </p>
      </div>
    )
  }

  return (
    <div className="kb">
      <aside className="kb__spaces" aria-label="Пространства">
        <div className="kb__search">
          <Icon name="search" size={16} aria-hidden="true" />
          <input
            className="kb__search-input"
            type="search"
            value={query}
            placeholder="Поиск по статьям"
            aria-label="Поиск по статьям"
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {query.trim().length >= 2 ? (
          <SearchResults
            results={results}
            loading={searching}
            spaces={spaces}
            onOpen={openHit}
          />
        ) : (
          <ul className="kb__space-list">
            {spaces.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className={`kb__space${s.key === spaceKey ? ' is-active' : ''}`}
                  onClick={() => navigate(`/admin/kb/${s.key}`)}
                  aria-current={s.key === spaceKey ? 'true' : undefined}
                >
                  <Icon name={s.icon || 'file'} size={16} aria-hidden="true" />
                  <span className="kb__space-name">{s.name}</span>
                  <span className="kb__space-key">{s.key}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <nav className="kb__tree" aria-label="Страницы пространства">
        <header className="kb__tree-head">
          <span className="kb__tree-title">{space?.name || '—'}</span>
          {space?.my_level && (
            <span className="kb__level" title="Ваш уровень доступа">
              {LEVEL_LABELS[space.my_level]}
            </span>
          )}
        </header>

        {(canWrite || canManage) && (
          <div className="kb__tools">
            {canWrite && (
              <button type="button" className="btn btn--ghost btn--sm" disabled={creating}
                onClick={() => createPage(null)}>
                <Icon name="plus" size={15} aria-hidden="true" />
                Статья
              </button>
            )}
            {canManage && (
              <>
                <button
                  type="button"
                  className={`btn btn--ghost btn--sm${mode === 'review' ? ' is-active' : ''}`}
                  onClick={() => setMode(mode === 'review' ? 'read' : 'review')}
                >
                  На проверке
                  {pendingCount > 0 && <span className="kb__badge">{pendingCount}</span>}
                </button>
                <button type="button" className="btn btn--ghost btn--sm"
                  onClick={() => setShowSettings(true)}>
                  <Icon name="users" size={15} aria-hidden="true" />
                  Доступ
                </button>
              </>
            )}
          </div>
        )}

        {treeLoading && <div className="kb__tree-loading"><div className="spinner" /></div>}
        {treeError && <p className="kb__error">{treeError}</p>}
        {!treeLoading && !treeError && (
          <PageTree
            nodes={tree}
            activeId={activeId}
            onSelect={openPage}
            emptyHint="В пространстве пока нет статей"
          />
        )}
      </nav>

      <main className="kb__content">
        {mode === 'review' && canManage && (
          <ReviewQueue
            onOpenPage={(item) => {
              setMode('read')
              navigate(`/admin/kb/${spaceKey}/${item.page_id}`)
            }}
          />
        )}

        {mode === 'edit' && page && (
          <ArticleEditor
            page={page}
            level={level}
            onDone={() => { setMode('read'); reloadPage(); reloadTree() }}
            onCancel={() => { setMode('read'); reloadPage() }}
          />
        )}

        {mode === 'read' && !activeId && !pageLoading && (
          <div className="kb-state kb-state--inline">
            <Icon name="file" size={28} aria-hidden="true" />
            <p className="kb-state__title">Выберите статью слева</p>
            <p className="kb-state__hint">Или найдите нужную через поиск.</p>
          </div>
        )}
        {mode === 'read' && pageLoading && (
          <div className="kb__tree-loading"><div className="spinner" /></div>
        )}
        {mode === 'read' && pageError && (
          <div className="kb-state kb-state--inline">
            <Icon name="warning" size={28} aria-hidden="true" />
            <p className="kb-state__title">{pageError}</p>
          </div>
        )}
        {mode === 'read' && page && !pageLoading && (
          <>
            {(canWrite || canManage) && (
              <div className="kb__page-tools">
                <button type="button" className="btn btn--secondary btn--sm"
                  onClick={() => setMode('edit')}>
                  <Icon name="wrench" size={15} aria-hidden="true" />
                  {canManage ? 'Редактировать' : 'Предложить правку'}
                </button>
                <button type="button" className="btn btn--ghost btn--sm"
                  onClick={() => setShowHistory(true)}>
                  История
                </button>
                {canManage && (
                  <>
                    <button type="button" className="btn btn--ghost btn--sm"
                      onClick={togglePinned}>
                      {page.pinned ? 'Открепить' : 'Закрепить'}
                    </button>
                    <button type="button" className="btn btn--ghost btn--sm"
                      onClick={() => createPage(page.id)}>
                      Вложенная статья
                    </button>
                    <button type="button" className="btn btn--ghost btn--sm kb__danger"
                      onClick={archivePage}>
                      В архив
                    </button>
                  </>
                )}
              </div>
            )}
            {page.my_revision?.status === 'rejected' && (
              <div className="kb__returned">
                <Icon name="warning" size={16} aria-hidden="true" />
                <div>
                  <strong>Вашу правку вернули на доработку.</strong>
                  <p>{page.my_revision.review_comment}</p>
                </div>
              </div>
            )}
            {page.my_revision?.status === 'pending' && (
              <p className="kb__pending">Ваша правка ждёт проверки.</p>
            )}
            <ArticleView page={page} breadcrumbs={breadcrumbs} onNavigate={openPage} />
          </>
        )}
      </main>

      {showHistory && page && (
        <HistoryPanel
          page={page}
          level={level}
          onClose={() => setShowHistory(false)}
          onReverted={() => { setShowHistory(false); reloadPage(); reloadTree() }}
        />
      )}
      {showSettings && spaceKey && (
        <SpaceSettings spaceKey={spaceKey} onClose={() => setShowSettings(false)} />
      )}
    </div>
  )
}

const LEVEL_LABELS = {
  read: 'Чтение',
  write: 'Правка',
  admin: 'Управление',
}

function SearchResults({ results, loading, spaces, onOpen }) {
  if (loading) {
    return <div className="kb__tree-loading"><div className="spinner" /></div>
  }
  if (!results.length) {
    return <p className="kb__search-empty">Ничего не найдено. Попробуйте другие слова.</p>
  }
  return (
    <ul className="kb__hits">
      {results.map((hit) => {
        const space = spaces.find((s) => s.id === hit.space_id)
        return (
          <li key={hit.page_id}>
            <button type="button" className="kb__hit" onClick={() => onOpen(hit)}>
              <span className="kb__hit-title">{hit.title}</span>
              {space && <span className="kb__hit-space">{space.name}</span>}
              <span className="kb__hit-snippet">{stripMarks(hit.snippet)}</span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}

/** Сниппет приходит с подсветкой PostgreSQL — показываем как текст. */
function stripMarks(snippet) {
  return String(snippet || '').replace(/<\/?b>/g, '')
}
